import clsx from "clsx";
import { useMemo, useState } from "react";

import { Product } from "@transfr-inc/dashboard-components/constants";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownItems,
} from "@transfr-inc/dashboard-components/forms";
import { getProduct } from "@transfr-inc/dashboard-components/utils";

import { useMultiProductClassroomEnabled } from "@hooks/queries/account-selectors";
import { ProductDropdownOption } from "./product-dropdown-option";

import "./product-dropdown-list.scss";

export const TrekTrainProduct = { id: "TRK_TS", displayName: "Trek + Train" };

// Basics is a UI-only pseudo-product: it has no backend product id of its own
// and is sent to the backend as Product.PROG plus the `isProfessionalLearning`
// flag. `id` and `displayName` derive from Product.BASICS; `name` and
// `description` are pinned locally to the instructor-facing copy shown in the
// product dropdown and the program banner, diverging from the library object
// (whose `name` is "BASICS").
export const ProfessionalLearningProduct = {
  ...getProduct(Product.BASICS),
  name: "Basics",
  description:
    "Empowering instructors with a hands-on experience to master our tools, accelerate onboarding, and grow professionally.",
};

export function ProductDropDownList(props) {
  const {
    placeholder = "Select a Product",
    products,
    className,
    onProductSelected,
    defaultProduct,
    selectedProduct: controlledSelected,
    combineCeTrek = false,
    productImages = false,
    clearEnabled,
    disabled = false,
  } = props;
  const isMultiProductClassroomEnabled = useMultiProductClassroomEnabled();
  const [internalSelected, setInternalSelected] = useState(
    getProduct(defaultProduct)
  );
  const isControlled = "selectedProduct" in props;
  const selectedProduct = isControlled ? controlledSelected : internalSelected;

  const onOptionSelected = (option) => {
    if (!isControlled) {
      setInternalSelected(option);
    }

    onProductSelected(option);
  };

  const productOptions = useMemo(() => {
    const displayProducts = products?.filter(
      (p) => !combineCeTrek || (p !== Product.CE && p !== Product.TRK)
    );
    const hasCeTrek = products.some(
      (p) => p === Product.CE || p === Product.TRK
    );

    if (combineCeTrek && hasCeTrek) {
      displayProducts.push(Product.CE_TRK);
    }

    const hasTrekTrain = hasCeTrek && products.some((p) => p === Product.TS);
    if (isMultiProductClassroomEnabled && combineCeTrek && hasTrekTrain) {
      displayProducts.push(TrekTrainProduct);
    }

    return displayProducts.map((p) => {
      const product = typeof p === "object" ? p : getProduct(p);

      if (product) {
        return (
          <DropdownItem
            className="product-dropdown-option"
            key={`product-${product.name ?? product.id}`}
            value={product}
          >
            <ProductDropdownOption
              product={product}
              productImagesEnabled={productImages}
            />
          </DropdownItem>
        );
      }
    });
  }, [products]);

  return (
    <div className={clsx("product-drop-down-list", className)}>
      <Dropdown
        selected={selectedProduct}
        onOptionSelected={onOptionSelected}
        className="product-dropdown"
        disabled={disabled}
      >
        <DropdownButton
          placeholder={placeholder}
          required
          clearEnabled={clearEnabled}
        >
          {selectedProduct && (
            <ProductDropdownOption
              product={selectedProduct}
              productImagesEnabled={productImages}
            />
          )}
        </DropdownButton>
        <DropdownItems minHeight={150}>{productOptions}</DropdownItems>
      </Dropdown>
    </div>
  );
}
